/**
 * Reduced Motion Fallback Hook
 *
 * Provides accessibility support for users who prefer reduced motion.
 * When enabled, the Letter Throwing animation is skipped entirely
 * and the text appears instantly.
 *
 * Implements WCAG 2.1 SC 2.3.3 (Animation from Interactions)
 */

import { useState, useEffect, useCallback } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';

/**
 * Hook to detect reduced motion preference.
 *
 * @returns {boolean} True if user prefers reduced motion
 */
export function useReducedMotionFallback(): boolean {
  const [isReducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    // Initial check
    const checkReducedMotion = async () => {
      try {
        const enabled = await AccessibilityInfo.isReduceMotionEnabled();
        setReducedMotion(enabled);
      } catch (error) {
        // Web fallback: check media query
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
          setReducedMotion(mediaQuery.matches);
        }
      }
    };

    checkReducedMotion();

    // Subscribe to changes
    const subscription = AccessibilityInfo.addEventListener(
      'reduceMotionChanged',
      setReducedMotion
    );

    // Web: also listen to media query changes
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
      mediaQuery.addEventListener('change', handleChange);

      return () => {
        subscription.remove();
        mediaQuery.removeEventListener('change', handleChange);
      };
    }

    return () => subscription.remove();
  }, []);

  return isReducedMotion;
}

/**
 * Hook that provides reduced motion-aware animation wrappers.
 *
 * Usage:
 * ```tsx
 * const { shouldAnimate, FallbackView } = useAnimationWithFallback();
 *
 * if (!shouldAnimate) {
 *   return <FallbackView>{children}</FallbackView>;
 * }
 * ```
 */
export function useAnimationWithFallback() {
  const isReducedMotion = useReducedMotionFallback();

  const shouldAnimate = !isReducedMotion;

  /**
   * Wrapper that applies fallback behavior.
   * When reduced motion is enabled, children render instantly with full opacity.
   */
  const FallbackBehavior = useCallback(
    ({ onComplete }: { onComplete?: () => void }) => {
      useEffect(() => {
        // Immediately trigger completion callback
        if (isReducedMotion && onComplete) {
          // Small delay to ensure React has rendered
          const timeout = setTimeout(onComplete, 50);
          return () => clearTimeout(timeout);
        }
      }, [onComplete]);

      return null;
    },
    [isReducedMotion]
  );

  return {
    shouldAnimate,
    isReducedMotion,
    FallbackBehavior,
  };
}

export type UseReducedMotionReturn = ReturnType<typeof useReducedMotionFallback>;
export type UseAnimationWithFallbackReturn = ReturnType<typeof useAnimationWithFallback>;
